import { ChangeDetectionStrategy, Component, effect, ElementRef, OnDestroy, Signal, viewChild, signal, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectComponentsConfigForType } from '../../../state';
import { BaseComponentTypeEnum, ComponentModel, HeaderComponentConfigModel, UploadCategoryEnum } from '@tailormap-viewer/api';
import { CssHelper } from '@tailormap-viewer/shared';
import { HeaderHelper } from './header.helper';
import { ImageWithDescriptionComponent } from '@tailormap-viewer/shared';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'tm-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ImageWithDescriptionComponent,
        MatIconButton,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatMenuItem,
    ],
})
export class HeaderComponent implements OnDestroy {
  private store$ = inject(Store);

  public mobileHeader = input<boolean>(false);

  public config: Signal<ComponentModel<HeaderComponentConfigModel> | null>;
  public headerContainer = viewChild<ElementRef<HTMLDivElement>>('headerContainer');
  public measurementContainer = viewChild<ElementRef<HTMLDivElement>>('measurementContainer');

  public useDropdownMenu = signal(false);
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    this.config = this.store$.selectSignal(selectComponentsConfigForType(BaseComponentTypeEnum.HEADER));

    effect(() => {
      const config = this.config();
      const headerContainer = this.headerContainer();
      const mobileHeader = this.mobileHeader();
      if (config && config.config && headerContainer) {
        const configuredHeight = config.config.height || 100;
        const componentHeight = mobileHeader ? Math.min(50, configuredHeight) : configuredHeight;
        CssHelper.setCssVariableValue('--header-component-height', componentHeight + 'px');
        CssHelper.setCssVariableValue('--header-text-color', config.config.textColor || '#000000', headerContainer.nativeElement);
        CssHelper.setCssVariableValue('--header-background-color', config.config.backgroundColor || '#ffffff', headerContainer.nativeElement);
      }
      this.setCss(config?.config?.css || '', headerContainer);
      this.setupResizeObserver();
    });
  }

  private setupResizeObserver(): void {
    const headerContainer = this.headerContainer()?.nativeElement;
    this.cleanupResizeObserver();
    if (!headerContainer) {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || entry.target !== headerContainer) {
        return;
      }
      this.checkUseDropdown();
    });
    this.resizeObserver.observe(headerContainer);
    this.checkUseDropdown();
  }

  public checkUseDropdown() {
    const headerContainer = this.headerContainer()?.nativeElement;
    const measurementContainer = this.measurementContainer()?.nativeElement;
    if (headerContainer && measurementContainer) {
      this.useDropdownMenu.set(HeaderHelper.shouldUseDropdownMenu(headerContainer, measurementContainer));
    }
  }

  public getUrl(url: string) {
    return `/api/uploads/${UploadCategoryEnum.HEADER_LOGO}/${url}/logo`;
  }

  private setCss(css: string, headerContainer?: ElementRef<HTMLDivElement>) {
    if (!headerContainer) {
      this.cleanUpCss();
      return;
    }
    HeaderHelper.setHeaderComponentCss(css, headerContainer.nativeElement);
  }

  public ngOnDestroy() {
    this.cleanUpCss();
    this.cleanupResizeObserver();
  }

  private cleanUpCss() {
    const styleSheet = document.querySelector<HTMLStyleElement>('#header-component-css');
    if (styleSheet && styleSheet.parentElement) {
      styleSheet.parentElement.removeChild(styleSheet);
    }
  }

  private cleanupResizeObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

}
